import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import PatternPad from '../components/PatternPad';

const API = process.env.REACT_APP_BACKEND_URL;

// Checklist de inspección — 4 estados por item
const CHECKLIST_OPTIONS = [
  { value: 'si', shortLabel: 'Sí', activeCls: 'bg-emerald-600 text-white border-emerald-600' },
  { value: 'no', shortLabel: 'No', activeCls: 'bg-rose-600 text-white border-rose-600' },
  { value: 'cant_check', shortLabel: 'No revisable', activeCls: 'bg-slate-600 text-white border-slate-600' },
  { value: 'broken_works', shortLabel: 'Roto/funcional', activeCls: 'bg-amber-500 text-white border-amber-500' },
];

export default function RepairForm() {
  const { getAuthHeaders, currentBranch } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [branches, setBranches] = useState([]);
  const [models, setModels] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [waitTimeOptions, setWaitTimeOptions] = useState([]);
  // Client search
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSelected, setClientSelected] = useState(false);
  // Model search
  const [modelSearchQuery, setModelSearchQuery] = useState('');
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);

  const [form, setForm] = useState({
    client_id: '', client_name: '', client_phone: '', contact_phone: '',
    device_model: '', problem_description: '',
    estimated_cost: '', wait_time: '', branch_id: currentBranch || '', checklist: {},
    unlock_pin: '', unlock_pattern: [],
    bag: '',
  });
  // Bolsa availability check
  const [bagCheck, setBagCheck] = useState({ status: 'idle', message: '' });
  const bagDebounceRef = useRef(null);

  useEffect(() => {
    fetchBranches(); fetchModels(); fetchChecklist(); fetchWaitTimes();
    // Pre-fill client if coming from Clients page
    if (location.state?.client) {
      const c = location.state.client;
      setForm(f => ({ ...f, client_id: c.id || '', client_name: c.name, client_phone: c.phone, contact_phone: c.phone2 || '' }));
      setClientSelected(true);
      setClientSearchQuery(c.phone + ' - ' + c.name);
    }
  }, []);

  const fetchBranches = async () => {
    try { const res = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() }); setBranches(res.data); } catch {}
  };
  const fetchModels = async () => {
    try { const res = await axios.get(`${API}/api/settings/compatible_models`, { headers: getAuthHeaders() }); setModels((res.data?.value || []).sort((a, b) => a.localeCompare(b))); } catch { setModels([]); }
  };
  const fetchChecklist = async () => {
    try { const res = await axios.get(`${API}/api/settings/repair_checklist`, { headers: getAuthHeaders() }); setChecklistItems(res.data?.value || []); } catch { setChecklistItems([]); }
  };
  const fetchWaitTimes = async () => {
    try { const res = await axios.get(`${API}/api/settings/wait_times`, { headers: getAuthHeaders() }); setWaitTimeOptions(res.data?.value || []); } catch { setWaitTimeOptions([]); }
  };

  const searchClients = async (query) => {
    setClientSearchQuery(query);
    if (!query.trim() || query.trim().length < 2) { setClientSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/api/clients?search=${encodeURIComponent(query)}`, { headers: getAuthHeaders() });
      setClientSearchResults(res.data);
    } catch {}
  };

  const selectClient = (client) => {
    setForm({ ...form, client_id: client.id, client_name: client.name, client_phone: client.phone, contact_phone: client.phone2 || '' });
    setClientSelected(true);
    setClientSearchQuery(client.phone + ' - ' + client.name);
    setClientSearchResults([]);
  };

  const clearClient = () => {
    setForm({ ...form, client_id: '', client_name: '', client_phone: '', contact_phone: '' });
    setClientSelected(false);
    setClientSearchQuery('');
    setClientSearchResults([]);
  };

  const checkBagAvailability = (rawBag) => {
    if (bagDebounceRef.current) clearTimeout(bagDebounceRef.current);
    const trimmed = String(rawBag || '').trim();
    if (!trimmed) { setBagCheck({ status: 'idle', message: '' }); return; }
    if (!/^\d+$/.test(trimmed) || parseInt(trimmed, 10) < 1) {
      setBagCheck({ status: 'invalid', message: 'Debe ser un numero entero mayor a 0' });
      return;
    }
    setBagCheck({ status: 'checking', message: 'Verificando...' });
    bagDebounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/repairs/check-bag?bag=${trimmed}`, { headers: getAuthHeaders() });
        if (r.data.available) {
          setBagCheck({ status: 'available', message: `Bolsa #${trimmed} disponible` });
        } else {
          const c = r.data.conflict || {};
          setBagCheck({
            status: 'taken',
            message: `Bolsa #${trimmed} en uso por ${c.ticket || ''} (${c.client_name || ''})`,
          });
        }
      } catch {
        setBagCheck({ status: 'invalid', message: 'Error al verificar' });
      }
    }, 350);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (bagCheck.status === 'taken' || bagCheck.status === 'invalid' || bagCheck.status === 'checking') {
      return;
    }
    try {
      const payload = {
        client_id: form.client_id, client_name: form.client_name,
        client_phone: form.client_phone, contact_phone: form.contact_phone,
        device_model: form.device_model, problem_description: form.problem_description,
        estimated_cost: parseFloat(form.estimated_cost) || 0,
        wait_time: form.wait_time, branch_id: form.branch_id, checklist: form.checklist,
        unlock_pin: form.unlock_pin || '',
        unlock_pattern: Array.isArray(form.unlock_pattern) ? form.unlock_pattern : [],
      };
      const bagStr = String(form.bag || '').trim();
      if (bagStr) payload.bag = parseInt(bagStr, 10);
      await axios.post(`${API}/api/repairs`, payload, { headers: getAuthHeaders() });
      navigate('/repairs');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear la reparacion');
    }
  };

  return (
    <div data-testid="repair-form-page">
      <Link to="/repairs" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1D3C6E] mb-4 transition-colors no-underline">
        <ArrowLeft className="w-4 h-4" /> Volver a reparaciones
      </Link>
      <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit'] mb-6">Nueva Reparacion</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Client & Device */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Info */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 font-['Outfit']">Datos del Cliente</h3>
              <div className="space-y-4">
                <div className="relative">
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Buscar cliente</label>
                  {clientSelected ? (
                    <div className="flex items-center gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                      <span className="text-sm font-medium flex-1">{form.client_name} - {form.client_phone}</span>
                      <button type="button" onClick={clearClient} className="text-xs text-red-500 hover:text-red-700">Cambiar</button>
                    </div>
                  ) : (
                    <>
                      <Input data-testid="repair-client-search" placeholder="Buscar por telefono o nombre..." value={clientSearchQuery} onChange={(e) => searchClients(e.target.value)} />
                      {clientSearchResults.length > 0 && (
                        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-md max-h-32 overflow-y-auto">
                          {clientSearchResults.map(c => (
                            <div key={c.id} onClick={() => selectClient(c)} className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                              <p className="text-sm font-medium">{c.name}</p>
                              <p className="text-xs text-slate-500">{c.phone}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-xs font-medium text-slate-600">Nombre *</label><Input data-testid="repair-client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required /></div>
                  <div><label className="text-xs font-medium text-slate-600">Telefono personal *</label><Input data-testid="repair-client-phone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} required /></div>
                </div>
                <div><label className="text-xs font-medium text-slate-600">Telefono de contacto (si es diferente)</label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="Opcional" /></div>
              </div>
            </div>

            {/* Device & Problem */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 font-['Outfit']">Dispositivo y Problema</h3>
              <div className="space-y-4">
                {/* Bolsa - real-time uniqueness check */}
                <div>
                  <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                    Bolsa <span className="text-[10px] text-slate-400 font-normal">(numero único entre reparaciones activas)</span>
                  </label>
                  <div className="relative">
                    <Input
                      data-testid="repair-bag"
                      type="number"
                      min="1"
                      step="1"
                      placeholder="Ej: 12"
                      value={form.bag}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9]/g, '');
                        setForm({ ...form, bag: v });
                        checkBagAvailability(v);
                      }}
                      className={
                        bagCheck.status === 'taken' || bagCheck.status === 'invalid'
                          ? 'border-rose-400 focus-visible:ring-rose-400 pr-9'
                          : bagCheck.status === 'available'
                            ? 'border-emerald-400 focus-visible:ring-emerald-400 pr-9'
                            : 'pr-9'
                      }
                    />
                    <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                      {bagCheck.status === 'checking' && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                      {bagCheck.status === 'available' && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                      {(bagCheck.status === 'taken' || bagCheck.status === 'invalid') && <AlertCircle className="w-4 h-4 text-rose-500" />}
                    </div>
                  </div>
                  {bagCheck.message && (
                    <p className={`text-xs mt-1 ${
                      bagCheck.status === 'available' ? 'text-emerald-600'
                        : bagCheck.status === 'taken' || bagCheck.status === 'invalid' ? 'text-rose-600'
                        : 'text-slate-500'
                    }`} data-testid="repair-bag-feedback">
                      {bagCheck.message}
                    </p>
                  )}
                </div>
                {/* Model - searchable */}
                <div className="relative">
                  <label className="text-xs font-medium text-slate-600">Modelo *</label>
                  {form.device_model && !modelDropdownOpen ? (
                    <div className="flex items-center gap-2 p-2.5 border border-slate-200 rounded-lg bg-slate-50">
                      <span className="text-sm font-medium flex-1">{form.device_model}</span>
                      <button type="button" onClick={() => { setForm({ ...form, device_model: '' }); setModelSearchQuery(''); setModelDropdownOpen(true); }} className="text-xs text-red-500 hover:text-red-700">Cambiar</button>
                    </div>
                  ) : (
                    <>
                      <Input data-testid="repair-model-search" placeholder="Buscar modelo..." value={modelSearchQuery}
                        onChange={(e) => { setModelSearchQuery(e.target.value); setModelDropdownOpen(true); }}
                        onFocus={() => setModelDropdownOpen(true)} />
                      {modelDropdownOpen && (
                        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-md max-h-40 overflow-y-auto">
                          {models.filter(m => {
                            if (!modelSearchQuery.trim()) return true;
                            const words = modelSearchQuery.toLowerCase().split(/\s+/).filter(Boolean);
                            return words.every(w => m.toLowerCase().includes(w));
                          }).map(m => (
                            <div key={m} onClick={() => { setForm({ ...form, device_model: m }); setModelSearchQuery(''); setModelDropdownOpen(false); }}
                              className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0 text-sm">{m}</div>
                          ))}
                          {models.filter(m => { const words = (modelSearchQuery || '').toLowerCase().split(/\s+/).filter(Boolean); return !words.length || words.every(w => m.toLowerCase().includes(w)); }).length === 0 && (
                            <div className="px-3 py-2 text-xs text-slate-400 text-center">Sin resultados</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div><label className="text-xs font-medium text-slate-600">Descripcion del problema *</label>
                  <textarea data-testid="repair-problem" className="w-full border border-slate-200 rounded-lg p-2.5 text-sm min-h-[100px]" value={form.problem_description} onChange={(e) => setForm({ ...form, problem_description: e.target.value })} required />
                </div>
                <div><label className="text-xs font-medium text-slate-600">Presupuesto</label>
                  <Input type="number" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} placeholder="$0.00" />
                </div>
              </div>
            </div>
          </div>

          {/* Right column - Settings & Checklist */}
          <div className="space-y-6">
            {/* Time & Branch */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 font-['Outfit']">Configuracion</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-600">Tiempo de espera</label>
                  <Select value={form.wait_time} onValueChange={(val) => setForm({ ...form, wait_time: val })}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>{waitTimeOptions.map(wt => <SelectItem key={wt} value={wt}>{wt}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Sucursal</label>
                  <Select value={form.branch_id} onValueChange={(val) => setForm({ ...form, branch_id: val })}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Checklist */}
            {checklistItems.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4 font-['Outfit']">Checklist de Inspección</h3>
                <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="repair-create-checklist-grid">
                  <div className="grid grid-cols-[1fr_repeat(4,minmax(0,1fr))] gap-px bg-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    <div className="bg-slate-50 px-2 py-1.5">Item</div>
                    {CHECKLIST_OPTIONS.map(opt => (
                      <div key={opt.value} className="bg-slate-50 px-1 py-1.5 text-center">{opt.shortLabel}</div>
                    ))}
                  </div>
                  <div className="bg-white">
                    {checklistItems.map((item, idx) => {
                      const current = form.checklist?.[item] || null;
                      return (
                        <div key={idx} className="grid grid-cols-[1fr_repeat(4,minmax(0,1fr))] gap-px bg-slate-100 border-t border-slate-100 first:border-t-0">
                          <div className="bg-white px-2 py-2 text-sm text-slate-700">{item}</div>
                          {CHECKLIST_OPTIONS.map(opt => {
                            const active = current === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setForm(prev => ({
                                  ...prev,
                                  checklist: { ...(prev.checklist || {}), [item]: active ? null : opt.value },
                                }))}
                                className={`bg-white py-1.5 px-1 text-[11px] font-medium border-2 transition-colors ${
                                  active ? opt.activeCls : 'border-transparent text-slate-400 hover:bg-slate-50'
                                }`}
                                aria-label={`${item}: ${opt.shortLabel}`}
                                data-testid={`repair-create-checklist-${idx}-${opt.value}`}
                                title={opt.shortLabel}
                              >
                                {active ? '●' : '○'}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Credenciales de Desbloqueo */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-1 font-['Outfit']">Credenciales de Desbloqueo</h3>
              <p className="text-xs text-slate-500 mb-4">Ambos campos son opcionales.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Clave</label>
                  <Input
                    data-testid="repair-unlock-pin"
                    value={form.unlock_pin}
                    onChange={(e) => setForm({ ...form, unlock_pin: e.target.value })}
                    placeholder="PIN o contraseña alfanumérica"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Patrón</label>
                  <PatternPad
                    value={form.unlock_pattern}
                    onChange={(p) => setForm({ ...form, unlock_pattern: p })}
                    testid="repair-unlock-pattern"
                  />
                </div>
              </div>
            </div>

            <Button type="submit" disabled={bagCheck.status === 'taken' || bagCheck.status === 'invalid' || bagCheck.status === 'checking'} className="w-full bg-[#1D3C6E] hover:bg-[#152B50] py-2.5 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-repair-button">Registrar Reparacion</Button>
          </div>
        </div>
      </form>
    </div>
  );
}
